import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import { ADMIN_NAV_SECTION, NAV_SECTIONS, WORKER_NAV_SECTIONS, getVisibleNavigationSections, hasManagementAccess, hasWorkerAccess } from '../constants/management';
import { getPagePermission } from '../constants/permissions';
import { applyUiTheme, isUiTheme, UI_THEME_STORAGE_KEY, UI_THEME_OPTIONS, UiTheme } from '../constants/uiTheme';
import { normalizeRoleNames } from '../utils/roleNames';
import api from '../api/api';
import { getOperatingContext, isOperatingContextReady, useOperatingContext } from '../utils/operatingContextLifecycle';
import { useOperatingContextActions } from './OperatingContextProvider';
import { SupplierBankingReviewQueue } from '../types';
import NotificationBell from './NotificationBell';

const Navbar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, username, roles, permissions } = useAppSelector((s) => s.auth);
  const normalizedRoles = normalizeRoleNames(roles);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [currentTheme, setCurrentTheme] = useState<UiTheme>('light');
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [dropdownLeft, setDropdownLeft] = useState(0);
  const [bankingAttention, setBankingAttention] = useState<{ pending: number; overdue: number; generation: number } | null>(null);
  const context = useOperatingContext();
  const { select, retry } = useOperatingContextActions();
  const operatingGroups = context.token === token ? context.groups : [];
  const selectedOperatingGroupId = context.token === token ? context.groupId || '' : '';
  const canViewAllGroups = context.token === token && context.canViewAllGroups;
  const operatingGroupLoading = context.token !== token || context.status === 'initializing';
  const contextReady = isOperatingContextReady(context, token);
  const navbarRef = useRef<HTMLElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const themeRef = useRef<HTMLButtonElement | null>(null);

  const hasManagementRole = hasManagementAccess(normalizedRoles, permissions);
  const hasWorkerRole = hasWorkerAccess(normalizedRoles, permissions);
  const sections = hasManagementRole ? [...NAV_SECTIONS, ADMIN_NAV_SECTION]
    : hasWorkerRole ? WORKER_NAV_SECTIONS : NAV_SECTIONS.slice(0, 1);
  const activeSections = getVisibleNavigationSections(
    sections, permissions, selectedOperatingGroupId, context.restaurantAuditReadsEnabled);
  const bankingPermission = getPagePermission('/analytics/procurement-finance');
  const canReadBanking = !!bankingPermission && permissions.includes(bankingPermission);

  const closeMenus = () => {
    setMenuOpen(false);
    setActiveSection(null);
    setThemeMenuOpen(false);
  };

  useEffect(() => { closeMenus(); }, [location.key, token]);

  useLayoutEffect(() => {
    const positionDropdown = () => {
      const trigger = navbarRef.current?.querySelector<HTMLButtonElement>('.app-navbar__section-title[aria-expanded="true"]');
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = Math.min(320, window.innerWidth - 32);
      const left = Math.max(16, Math.min(rect.left, window.innerWidth - width - 16));
      const landscapeTablet = typeof window.matchMedia === 'function' &&
        window.matchMedia('(min-width: 769px) and (max-width: 900px) and (orientation: landscape)').matches;
      setDropdownLeft(landscapeTablet
        ? left
        : left - rect.left);
    };
    positionDropdown();
    window.addEventListener('resize', positionDropdown);
    return () => window.removeEventListener('resize', positionDropdown);
  }, [activeSection]);

  useEffect(() => {
    const savedTheme = localStorage.getItem(UI_THEME_STORAGE_KEY);
    setCurrentTheme(savedTheme && isUiTheme(savedTheme) ? savedTheme : 'light');
  }, []);

  const changeOperatingGroup = async (groupId: string) => {
    if (!groupId || groupId === selectedOperatingGroupId || operatingGroupLoading) return;
    if (selectedOperatingGroupId &&
        !window.confirm('Зміна групи перезавантажить CRM і закриє незбережені форми. Продовжити?')) return;
    await select(groupId);
    if (selectedOperatingGroupId && isOperatingContextReady(getOperatingContext(), token)) window.location.reload();
  };

  useEffect(() => {
    if (!contextReady || !canReadBanking) {
      setBankingAttention(null);
      return;
    }
    let active = true;
    api.get<SupplierBankingReviewQueue>('/procurementfinancial/banking-attention')
      .then(response => {
        if (active) setBankingAttention({
          pending: response.data.pendingCount, overdue: response.data.overdueCount, generation: context.generation,
        });
      })
      .catch(error => {
        if (active) setBankingAttention(null);
        if (error?.response?.status !== 401 && error?.response?.status !== 403)
          console.error('Не вдалося завантажити індикатор перевірки реквізитів.', error);
      });
    return () => { active = false; };
  }, [token, canReadBanking, contextReady, context.generation]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (event.target instanceof Node && !navbarRef.current?.contains(event.target)) closeMenus();
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  if (!token) return null;

  return (
    <nav
      ref={navbarRef}
      aria-label="Головна навігація"
      className={`app-navbar ${menuOpen ? 'app-navbar--open' : ''}`}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) closeMenus();
      }}
      onKeyDown={event => {
        if (event.key !== 'Escape') return;
        if (themeMenuOpen) {
          setThemeMenuOpen(false);
          themeRef.current?.focus();
        } else if (activeSection) {
          const trigger = navbarRef.current?.querySelector<HTMLButtonElement>('.app-navbar__section-title[aria-expanded="true"]');
          setActiveSection(null);
          trigger?.focus();
        } else if (menuOpen) {
          setMenuOpen(false);
          toggleRef.current?.focus();
        }
        event.stopPropagation();
      }}
    >
      <div className="app-navbar__top">
        <button ref={toggleRef} type="button" className="app-navbar__toggle"
          aria-label={menuOpen ? 'Закрити меню' : 'Відкрити меню'}
          aria-controls="primary-navigation" aria-expanded={menuOpen}
          onClick={() => { setMenuOpen(prev => !prev); setActiveSection(null); setThemeMenuOpen(false); }}>
          <span aria-hidden="true">{menuOpen ? '×' : '☰'}</span>
        </button>
        <Link to="/workspace" className="app-navbar__brand" aria-label="CRM4Chef — Мій робочий простір">CRM4Chef</Link>
        <div className="app-navbar__user">
          <span className="app-navbar__username">{username}</span>
          {normalizedRoles.length > 0 && <span className="app-navbar__role">({normalizedRoles[0]})</span>}
        </div>
        <label className="app-navbar__operating-group">
          <span>Операційна група</span>
          <select aria-label="Операційна група" value={selectedOperatingGroupId}
            disabled={operatingGroupLoading || (operatingGroups.length === 0 && !canViewAllGroups)}
            onChange={event => void changeOperatingGroup(event.target.value)}>
            <option value="">{operatingGroups.length ? 'Оберіть групу' : 'Групи недоступні'}</option>
            {operatingGroups.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
            {canViewAllGroups && <option value="all">Усі групи — огляд, лише читання</option>}
          </select>
        </label>
        {!operatingGroupLoading && operatingGroups.length > 1 && !selectedOperatingGroupId &&
          <span className="app-navbar__operating-group-warning" role="status">Оберіть робочу групу</span>}
        {context.status === 'error' && <span role="alert">
          {context.error} <button type="button" onClick={retry}>Повторити</button>
        </span>}
        {contextReady && <NotificationBell key={context.generation} />}
        <div className="app-navbar__theme">
          <button ref={themeRef} type="button" className="app-navbar__theme-toggle"
            aria-label="Змінити тему" aria-expanded={themeMenuOpen} aria-controls="theme-options"
            onClick={() => { setThemeMenuOpen(prev => !prev); setActiveSection(null); }}>
            <span aria-hidden="true">{currentTheme === 'light' ? '☀' : currentTheme === 'dark' ? '☾' : '◆'}</span>
          </button>
          {themeMenuOpen && (
            <div id="theme-options" className="app-navbar__theme-options" role="group" aria-label="Тема оформлення">
              {UI_THEME_OPTIONS.map(option => (
                <button key={option.value} type="button" aria-pressed={currentTheme === option.value}
                  onClick={() => {
                    applyUiTheme(option.value);
                    setCurrentTheme(option.value);
                    setThemeMenuOpen(false);
                    themeRef.current?.focus();
                  }}>
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="button" onClick={() => { dispatch(logout()); navigate('/login'); }} className="app-navbar__logout">Вийти</button>
      </div>
      <div id="primary-navigation" className={`app-navbar__links ${menuOpen ? 'app-navbar__links--open' : ''}`}>
        {activeSections.map((section, index) => {
          const expanded = activeSection === section.title;
          const sectionId = `navigation-section-${index}`;
          return (
            <div key={section.title} className={`app-navbar__section ${expanded ? 'app-navbar__section--open' : ''}`}>
              <button type="button" className="app-navbar__section-title" aria-controls={sectionId} aria-expanded={expanded}
                onClick={() => { setActiveSection(prev => prev === section.title ? null : section.title); setThemeMenuOpen(false); }}>
                <span>{section.title}</span>
                <span className="app-navbar__section-caret" aria-hidden="true">{expanded ? '▴' : '▾'}</span>
              </button>
              <div id={sectionId} style={expanded ? { left: dropdownLeft } : undefined}
                className={`app-navbar__section-links ${expanded ? 'app-navbar__section-links--open' : ''}`}>
                {section.links.map(item => (
                  <Link key={item.path} to={item.path} className="app-navbar__link"
                    aria-current={location.pathname === item.path ? 'page' : undefined}
                    data-permission-key={getPagePermission(item.path)} onClick={closeMenus}>
                    {item.label}
                    {item.path === '/analytics/procurement-finance' && contextReady && bankingAttention &&
                      bankingAttention.generation === context.generation && bankingAttention.pending > 0 && (
                      <span className={`app-navbar__attention ${bankingAttention.overdue > 0 ? 'app-navbar__attention--overdue' : ''}`}
                        title={`${bankingAttention.overdue} прострочених перевірок`}>
                        {bankingAttention.pending}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
};

export default Navbar;
